import { Component, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { forkJoin } from 'rxjs';
import { FORMLY_FIELD_TYPES, FORMLY_FIELD_WRAPPERS } from '@app/constants';
import { DialogOptions, DialogService, FeedbackService, ReportService } from '@app/core/services';
import { AccountType } from '@app/enums';
import { BulkAction, ColumnFilter, Filter, FilterConfig, SelectOption, TableColumn, TableConfig } from '@app/interfaces';
import { AccountSubcategory } from '@app/modules/fund-management/accounts/models/account-categories';
import { ParentAccount } from '@app/modules/fund-management/accounts/models/accounts';
import { BaseListComponent, TableComponent, TitleComponent } from '@app/shared/components';
import { FormComponent, FormMode } from '@app/shared/forms/components';
import { FiltersComponent } from '@app/shared/forms/components/filters/filters.component';
import { AccountStructureResolverData } from '@app/shared/resolvers';
import { AccountCategoryService, AccountStructureService } from '@app/shared/services';
import { isNil, mapArrayToSelectOptions } from '@app/utils';
import { ReadOnly } from '@app/utils/claims';
import * as Filters from '@app/utils/filters';
import { AccountMassUpdateModel } from './account-mass-update.model';
import { AccountMassUpdateService } from './account-mass-update.service';

@Component({
	selector: 'sb-account-mass-update',
	standalone: true,
	imports: [TitleComponent, MatCardModule, FiltersComponent, TableComponent, FormComponent],
	templateUrl: './account-mass-update.component.html',
	styleUrl: './account-mass-update.component.less',
	providers: [AccountMassUpdateService],
})
export class AccountMassUpdateComponent extends BaseListComponent<ParentAccount, AccountMassUpdateService> {
	@ViewChild(TableComponent) accountsTable: TableComponent;
	@ViewChild(FormComponent) formComponent: FormComponent;
	@ReadOnly() isReadOnly = true;

	pageTitle = 'Account Mass Update';
	entityName: string;
	listingPagePath: string;
	listArrayPropertyName = 'accounts';
	noDataMessage = 'No Accounts Found';
	private questionMarkMask: string;
	private fundIndex: number;
	private selectedFund: string;
	private hideFundEquity = false;
	private allSubcategories: AccountSubcategory[] = [];
	private readonly allAccountTypes = 0;

	filterOptions: FormlyFormOptions = {
		formState: {
			selects: {
				accountTypes: [],
			},
		},
	};

	filterConfig: FilterConfig<ParentAccount>[];

	filterFields: FormlyFieldConfig[];

	filters = {
		search: '',
		AccountNumber: '',
		AccountType: this.allAccountTypes,
		IsActive: true,
	};

	config: TableConfig<ParentAccount> = {
		columns: this.tableColumns,
		bulkActions: this.bulkActions,
		defaultSortField: 'AccountNumber',
		defaultSortDirection: 'asc',
		showRowSelect: true,
		showRowActions: false,
		editableRows: false,
		onRowClick: null,
		selectedId: null,
	};

	form = new UntypedFormGroup({});
	protected formTitle = 'Enter Account Changes';

	formFields: FormlyFieldConfig[] = [];

	formOptions: FormlyFormOptions = {
		formState: {
			mode: FormMode.Edit,
			readOnly: false,
			flyout: true,
			selects: {
				categories: [],
				subCategories: [],
			},
		},
	};

	private readonly initialModel: AccountMassUpdateModel = {
		AccountIds: [],
		CashAccountId: null,
		APAccountId: null,
		EquityAccountId: null,
		STEAccountId: null,
		AccountCategoryId: null,
		AccountSubCategoryId: null,
	};

	updateModel: AccountMassUpdateModel = { ...this.initialModel };

	constructor(
		protected readonly listService: AccountMassUpdateService,
		protected readonly dialog: MatDialog,
		protected readonly feedbackService: FeedbackService,
		protected readonly route: ActivatedRoute,
		protected readonly router: Router,
		protected readonly reportService: ReportService,
		private readonly accountStructureService: AccountStructureService,
		private readonly accountCategoryService: AccountCategoryService,
		private readonly dialogService: DialogService
	) {
		super(dialog, feedbackService, route, router, reportService);
	}

	protected afterOnInit(): void {
		this.loadFilterSetup();
		this.loadFormSetup();
		this.loadDependencies();
	}

	private loadFilterSetup(): void {
		const accountStructure: AccountStructureResolverData = this.route.snapshot.data['accountStructure'];
		this.questionMarkMask = accountStructure.QuestionMarkMask;
		this.fundIndex = accountStructure.AccountStructure.UnifiedElementFundIndex;
		this.filters.AccountNumber = this.questionMarkMask;
		this.filterConfig = this.getFilterConfig();
		this.filterFields = this.getFilterFields();
	}

	private loadFormSetup(): void {
		this.formFields = this.getFormFields();
	}

	private loadDependencies(): void {
		const accountTypes$ = this.accountStructureService.getAccountTypes();
		const categories$ = this.accountCategoryService.getCategories();
		const subCategories$ = this.accountCategoryService.getAllSubcategories();

		forkJoin([accountTypes$, categories$, subCategories$]).subscribe(([accountTypes, categoryData, subCategoryData]) => {
			this.filterOptions.formState.selects.accountTypes = mapArrayToSelectOptions(accountTypes.accountTypes, {
				value: 'Id',
				label: 'Description',
				all: true,
				allLabel: 'All',
				allValue: 0,
			});

			this.formOptions.formState.selects.categories = mapArrayToSelectOptions(categoryData.categories, {
				value: 'Id',
				label: 'Description',
				all: true,
				allLabel: '',
				allValue: null,
			});

			// Keep a list of all subcategories so we can quickly populate the subcategory select when a category is selected
			this.allSubcategories = subCategoryData.subcategories;
		});
	}

	private populateSubcategories(categoryId: number): void {
		if (isNil(categoryId)) {
			this.formOptions.formState.selects.subCategories = [];
			return;
		}

		this.formOptions.formState.selects.subCategories = mapArrayToSelectOptions(
			this.allSubcategories.filter((x) => x.CategoryId === categoryId),
			{
				value: 'Id',
				label: 'Description',
				all: true,
				allLabel: '',
				allValue: null,
			}
		);
	}

	protected get tableHasSelections(): boolean {
		return !isNil(this.accountsTable) && this.accountsTable.numSelected > 0;
	}

	protected onEnterChanges(accounts: ParentAccount[]): void {
		const distinctFunds = [...new Set(accounts.map((a: ParentAccount) => this.listService.getFund(a.AccountNumber, this.fundIndex)))];
		this.selectedFund = distinctFunds.length === 1 ? distinctFunds[0] : null;

		const accountsRequireFundEquity = accounts.find((a: ParentAccount) => a.AccountTypeId !== AccountType.Net || a.ClearAtYearEnd === true);
		this.hideFundEquity = !accountsRequireFundEquity;

		this.updateModel = { ...this.initialModel, AccountIds: accounts.map((a: ParentAccount) => a.Id) };
		this.formComponent.edit();
	}

	protected onSubmit(model: AccountMassUpdateModel): void {
		this.listService.saveAccountMassUpdate(model).subscribe((response) => {
			if (response.isValid) {
				this.updateModel = { ...model };
				// clear the flyout, resetting the form so we don't get an unsaved changes warning
				this.form.reset({ ...this.updateModel });
				this.formComponent.closeFlyout();

				this.dialogService.open({
					title: 'Account Mass Update Success',
					message: response.messages.join('<br>'),
					okText: 'OK',
					showCancel: false,
				} as DialogOptions);
			}
		});
	}

	protected get tableColumns(): TableColumn<ParentAccount>[] {
		return [
			{
				key: 'AccountNumber',
				header: 'Account Number',
				filter: ({ row, filterValue }: ColumnFilter<ParentAccount>): boolean =>
					filterValue === this.questionMarkMask || Filters.filterAccounts([row], filterValue).length > 0,
			},
			{
				key: 'Description',
				header: 'Description',
			},
			{
				key: 'AccountType',
				header: 'Account Type',
				filter: ({ row, filterValue }: ColumnFilter<ParentAccount>): boolean =>
					filterValue === this.allAccountTypes || row.AccountTypeId === filterValue,
			},
			{
				key: 'IsActive',
				header: 'Active',
				hidden: true,
				filter: ({ row, filterValue }: ColumnFilter<ParentAccount>): boolean => filterValue !== true || row.IsActive === true,
			},
		] as TableColumn<ParentAccount>[];
	}

	private get bulkActions(): BulkAction<any>[] {
		if (this.isReadOnly) {
			return [];
		}

		return [
			{
				label: 'Enter Changes',
				onClick: (selection: ParentAccount[]) => this.onEnterChanges(selection),
			},
		];
	}

	private getFilterConfig(): FilterConfig<ParentAccount>[] {
		return [
			{
				key: 'AccountNumber',
				label: 'Account Mask',
				clearedValue: this.questionMarkMask,
				transform: (fcFilter: Filter): string => (fcFilter.value === this.questionMarkMask ? null : fcFilter.value),
			},
			{
				key: 'AccountType',
				clearedValue: this.allAccountTypes,
				transform: (fcFilter: Filter): string =>
					fcFilter.value === this.allAccountTypes
						? null
						: this.filterOptions.formState.selects.accountTypes.find((t: SelectOption) => t.value === fcFilter.value)?.label,
			},
			{
				key: 'IsActive',
				label: 'Only Active Accounts',
				clearedValue: false,
				transform: (fcFilter: Filter): string => (fcFilter.value ? 'Yes' : null),
			},
		] as FilterConfig<ParentAccount>[];
	}

	private getFilterFields(): FormlyFieldConfig[] {
		return [
			{
				fieldGroupClassName: 'mass-update-filter-container',
				fieldGroup: [
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'AccountNumber',
								className: 'col-xs-12',
								type: FORMLY_FIELD_TYPES.maskedInput,
								props: {
									label: 'Account Mask',
									showMaskTyped: false,
									dropSpecialCharacters: false,
									questionMarkMask: this.questionMarkMask,
									maxLength: this.questionMarkMask.length,
								},
							},
						],
					},
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'AccountType',
								className: 'col-xs-12 linq-mt-3',
								type: FORMLY_FIELD_TYPES.select,
								props: {
									label: 'Account Type',
								},
								expressions: {
									'props.options': 'formState.selects.accountTypes',
								},
							},
						],
					},
					{
						fieldGroupClassName: 'row',
						fieldGroup: [
							{
								key: 'IsActive',
								className: 'col-xs-12 mat-checkbox-override-infix',
								type: FORMLY_FIELD_TYPES.checkbox,
								props: {
									label: 'Only Active Accounts',
									appearance: 'fill',
								},
							},
						],
					},
				],
			},
		] as FormlyFieldConfig[];
	}

	private getFormFields(): FormlyFieldConfig[] {
		// eslint-disable-next-line max-len
		const multipleFundWarning =
			'Accounts from multiple funds have been selected. Offsets other than sales tax expense must come from the same fund as the main account.';

		return [
			{
				wrappers: [FORMLY_FIELD_WRAPPERS.sectionSimple],
				props: {
					section: 'details',
				},
				fieldGroupClassName: 'row',
				fieldGroup: [
					{
						type: FORMLY_FIELD_TYPES.template,
						props: {
							template: `
										<p class="multiple-fund-warning-message">${multipleFundWarning}</p>
									`,
						},
						expressions: {
							hide: () => !isNil(this.selectedFund),
						},
					},
					this.getAccountInputFieldConfig('CashAccountId', 'Cash Account', false),
					this.getAccountInputFieldConfig('APAccountId', 'A/P Account', false),
					this.getAccountInputFieldConfig('EquityAccountId', 'Fund Equity Account', false),
					this.getAccountInputFieldConfig('STEAccountId', 'Sales Tax Expense', true),
					{
						key: 'AccountCategoryId',
						className: 'col-xs-12',
						type: FORMLY_FIELD_TYPES.select,
						props: {
							label: 'Account Category',
							required: false,
							change: (field: FormlyFieldConfig) => {
								field.form.get('AccountSubCategoryId').setValue(null);
								this.populateSubcategories(field.formControl.value);
							},
						},
						expressions: {
							'props.options': 'formState.selects.categories',
						},
					},
					{
						key: 'AccountSubCategoryId',
						className: 'col-xs-12',
						type: FORMLY_FIELD_TYPES.select,
						props: {
							label: 'Account Subcategory',
							required: false,
						},
						expressions: {
							'props.options': 'formState.selects.subCategories',
							'props.disabled': (field: FormlyFieldConfig) =>
								field.options.formState.selects.subCategories.length === 0 || isNil(field.model.AccountCategoryId),
						},
					},
				],
			},
		] as FormlyFieldConfig[];
	}

	private getAccountInputFieldConfig(key: string, label: string, isSalesTaxOffset: boolean): FormlyFieldConfig {
		return {
			key: key,
			className: 'col-xs-12',
			type: FORMLY_FIELD_TYPES.multiuseSearchInput,
			props: {
				label: label,
				placeholder: 'Search Accounts',
				hideLabel: false,
				required: false,
				readonly: false,
				defaultInputValue: '',
				floatLabel: 'always',
				dropdownProperties: ['AccountNumber', 'DescriptionValue'],
				dropdownTemplate: 'AccountNumber<br>Description: DescriptionValue',
				inputTemplate: 'AccountNumber',
				questionMarkMask: this.questionMarkMask,
				questionMarkMaskUserInputOnly: true,
				keyValueProperty: 'Id',
				filter: isSalesTaxOffset
					? (value: string) => this.listService.getSalesTaxAccountsForSearchInput(value)
					: (value: string) => this.listService.getOffsetAccountsForSearchInput(value, this.selectedFund, this.fundIndex),
			},
			expressions: {
				hide: key === 'EquityAccountId' ? () => this.hideFundEquity : false,
				'props.disabled': isSalesTaxOffset ? false : () => isNil(this.selectedFund),
			},
		} as FormlyFieldConfig;
	}
}
